import React from "react";

import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { Avatar, AvatarGroup, Box, Divider, Skeleton } from "@mui/material";
import { makeStyles } from "tss-react/mui";

import { IconButton } from "@/components/Button";
import { ShadowCard } from "@/components/Card/ShadowCard";
import {
  BodyBold,
  HeadlineBold,
  Overline1,
  Title1Bold,
} from "@/components/Typography";
import { colors } from "@/theme/overrides/colors";

const useStyles = makeStyles()({
  avatar: {
    position: "inherit",
    border: "2px solid white !important",
  },
  avatarCountContainer: {
    "&>div:first-of-type": {
      backgroundColor: colors.grayLightest,
      border: `2px dashed ${colors.grayLighter} !important`,
      fontSize: "12px",
      color: colors.text,
      fontWeight: 700,
    },
  },
});

interface ClientsSummaryCardProps {
  isLoading: boolean;
  title: string;
  avatars: (string | null | undefined)[] | undefined;
  clientsCount: number;
  weeklyCount?: number;
  monthlyCount?: number;
  onClick?: () => void;
}

export const ClientsSummaryCard: React.FC<ClientsSummaryCardProps> = ({
  isLoading,
  title,
  avatars,
  clientsCount,
  weeklyCount,
  monthlyCount,
  onClick,
}) => {
  const { classes } = useStyles();

  if (isLoading)
    return (
      <ShadowCard>
        <Box bgcolor={colors.theme.secondary.lightest} px="16px" py="8px">
          <HeadlineBold text={title} />
        </Box>
        <Box bgcolor={colors.mapped.surface.neutralWhite}>
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              px="16px"
              py="24px"
            >
              <Skeleton variant="circular" width={44} height={44} />
            </Box>
            <Divider />
            <Box display="flex">
              <Box flex={1} px="16px" py="6.5px">
                <Overline1 text="This week:" />
              </Box>
              <Box>
                <Divider orientation="vertical" />
              </Box>
              <Box flex={1} px="16px" py="6.5px">
                <Overline1 text="This month:" />
              </Box>
            </Box>
          </>
        </Box>
      </ShadowCard>
    );

  return (
    <ShadowCard boxShadow="0 4px 8px rgba(0, 0, 0, 0.05)">
      <Box bgcolor={colors.theme.secondary.lightest} px="16px" py="8px">
        <HeadlineBold text={title} />
      </Box>
      <Box bgcolor={colors.mapped.surface.neutralWhite}>
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            px="16px"
            py="24px"
          >
            <AvatarGroup
              className={classes.avatarCountContainer}
              total={clientsCount}
            >
              {avatars?.map((avatar, index) => {
                return (
                  <Avatar
                    key={index}
                    src={avatar ?? "/static/images/avatar.jpg"}
                    className={classes.avatar}
                  />
                );
              })}
            </AvatarGroup>
            <Box display="flex" alignItems="center">
              <Title1Bold text={clientsCount} />
              {onClick && (
                <IconButton
                  icon={<ChevronRightIcon htmlColor={colors.tealLight500} />}
                  size="smaller"
                  onClick={onClick}
                />
              )}
            </Box>
          </Box>
          {weeklyCount != undefined && monthlyCount !== undefined && (
            <>
              <Divider />
              <Box display="flex">
                <Box
                  alignItems="center"
                  display="flex"
                  flex={1}
                  justifyContent="space-between"
                  px="16px"
                  py="6.5px"
                >
                  <Overline1 text="This week:" />
                  <BodyBold text={weeklyCount} />
                </Box>
                <Box>
                  <Divider orientation="vertical" />
                </Box>
                <Box
                  alignItems="center"
                  display="flex"
                  flex={1}
                  justifyContent="space-between"
                  px="16px"
                  py="6.5px"
                >
                  <Overline1 text="This month:" />
                  <BodyBold text={monthlyCount} />
                </Box>
              </Box>
            </>
          )}
        </>
      </Box>
    </ShadowCard>
  );
};
